/**
 * Budget per account — pure hulpfuncties (formatteren, groeperen, invoer lezen).
 * De rekenregels zelf staan in de engine (services/budget.py); hier alleen weergave.
 */

export const MAANDNAMEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const EUR = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

/** € 12.345 — leeg bij null/undefined, zodat "niet ingevuld" iets anders is dan € 0. */
export function euro(waarde) {
  if (waarde === null || waarde === undefined || Number.isNaN(Number(waarde))) return '';
  return EUR.format(Math.round(Number(waarde)));
}

/** "12.500", "12500,50", "€ 1.000" → getal; leeg → null; onzin → NaN. */
export function leesBedrag(tekst) {
  if (tekst === null || tekst === undefined) return null;
  let s = String(tekst).replace(/[€\s]/g, '');
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/** Bedrag voor een invoerveld (zonder €, zonder decimalen als die er niet zijn). */
export function alsInvoer(waarde) {
  if (waarde === null || waarde === undefined) return '';
  const n = Math.round(Number(waarde) * 100) / 100;
  return Number.isInteger(n) ? String(n) : String(n).replace('.', ',');
}

export const SOORTEN = [
  { soort: 'account', label: 'Accounts' },
  { soort: 'nieuw', label: 'Nieuwe klanten (handmatig toegevoegd)' },
  { soort: 'leeg', label: 'Accounts zonder omzet of offertes' },
  { soort: 'zonder', label: 'Zonder account' },
  { soort: 'groep', label: 'Excel-groepen' },
  { soort: 'wees', label: 'Budget overzetten (telt niet mee)' },
];

/** Regels filteren op zoekterm en groeperen per soort; accounts op omzet vorig jaar. */
export function groepeer(regels, zoek, vorigJaar) {
  const z = (zoek || '').trim().toLowerCase();
  const passend = (regels || []).filter((r) => !z || [r.naam, ...(r.bedrijfsnamen || [])]
    .some((n) => (n || '').toLowerCase().includes(z)));
  const sleutel = String(vorigJaar - 1);
  return SOORTEN.map(({ soort, label }) => {
    const lijst = passend.filter((r) => r.soort === soort);
    if (soort !== 'groep') {
      lijst.sort((a, b) => ((b.realisatie?.[sleutel] || 0) + (b.forecast_vorig || 0))
        - ((a.realisatie?.[sleutel] || 0) + (a.forecast_vorig || 0)) || (a.naam || '').localeCompare(b.naam || ''));
    }
    return { soort, label, regels: lijst };
  }).filter((g) => g.regels.length > 0);
}

/**
 * Verdeel een bedrag (euro's) naar gewichten, in centen met grootste-rest — dezelfde regel als
 * de engine (services/budget.verdeel), zodat de maanden exact optellen tot het target.
 */
export function verdeelBedrag(totaal, gewichten) {
  const g = (gewichten && gewichten.length ? gewichten : Array(12).fill(1)).map((x) => Math.max(Number(x) || 0, 0));
  const som = g.reduce((a, b) => a + b, 0);
  const w = som > 0 ? g : g.map(() => 1);
  const s = som > 0 ? som : w.length;
  const cent = Math.round(Math.abs(Number(totaal) || 0) * 100);
  const teken = Number(totaal) < 0 ? -1 : 1;
  const ruw = w.map((x) => (cent * x) / s);
  const basis = ruw.map((x) => Math.floor(x));
  let rest = cent - basis.reduce((a, b) => a + b, 0);
  const volgorde = ruw.map((x, i) => [x - basis[i], i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let k = 0; k < volgorde.length && rest > 0; k += 1, rest -= 1) basis[volgorde[k][1]] += 1;
  return basis.map((c) => (teken * c) / 100);
}

/** Gat-weergave: positief = nog te vinden, ≤ 0 = gedekt, null = geen target. */
export function gatTekst(gat) {
  if (gat === null || gat === undefined) return { tekst: '', open: false };
  if (gat <= 0) return { tekst: 'gedekt', open: false };
  return { tekst: euro(gat), open: true };
}

/** Gewichten (fracties) ↔ procenten met één decimaal voor het invoerscherm. */
export const naarProcenten = (gewichten) => (gewichten || []).map((g) => Math.round(g * 1000) / 10);

export function uitProcenten(procenten) {
  const g = (procenten || []).map((p) => leesBedrag(p));
  if (g.length !== 12 || g.some((x) => x === null || Number.isNaN(x) || x < 0)) return null;
  const som = g.reduce((a, b) => a + b, 0);
  return som > 0 ? g.map((x) => x / som) : null;
}

/** Alleen de velden die echt veranderen (voorkomt lege opslagen en lege undo's). */
export function verschil(oud, nieuw) {
  const uit = {};
  Object.keys(nieuw).forEach((k) => {
    if (JSON.stringify(oud?.[k] ?? null) !== JSON.stringify(nieuw[k] ?? null)) uit[k] = nieuw[k];
  });
  return uit;
}

// Woorden die niets zeggen over welk account bij een bedrijf hoort (rechtsvorm, lidwoorden, …).
const STOPWOORDEN = new Set([
  'bv', 'nv', 'sa', 'vof', 'cv', 'bvba', 'gmbh', 'ltd', 'inc', 'holding', 'groep', 'group', 'nederland',
  'the', 'de', 'het', 'een', 'van', 'en', 'voor', 'stichting', 'gemeente', 'provincie', 'vereniging',
]);

/** "Schneider Electric S.A. / N.V" → ['schneider', 'electric']. */
export function naamWoorden(naam) {
  return (naam || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\b([a-z])\.(?=[a-z]\.?)/g, '$1')        // s.a. / n.v. / b.v. → sa / nv / bv
    .split(/[^a-z0-9]+/).filter((w) => w.length >= 2 && !STOPWOORDEN.has(w));
}

/**
 * Beste account voor een bedrijf zonder account, op gedeelde woorden in de accountnaam (telt
 * dubbel) en de namen van de bedrijven die er al onder hangen. Gelijkspel of niets gedeeld →
 * null: liever geen suggestie dan een verkeerde.
 */
export function koppelSuggestie(bedrijfsnaam, accounts) {
  const eigen = new Set(naamWoorden(bedrijfsnaam));
  if (!eigen.size) return null;
  const score = (a) => {
    const telt = (woorden) => new Set(woorden.filter((w) => eigen.has(w))).size;
    return 2 * telt(naamWoorden(a.naam)) + telt((a.bedrijfsnamen || []).flatMap(naamWoorden));
  };
  const scores = (accounts || []).map((a) => [score(a), a]).filter(([s]) => s > 0).sort((x, y) => y[0] - x[0]);
  if (!scores.length || (scores[1] && scores[1][0] === scores[0][0])) return null;
  return scores[0][1];
}

/** Accounts waaraan je een bedrijf kunt koppelen: ook de accounts zonder omzet (soort 'leeg'). */
export const koppelbareAccounts = (regels) => (regels || [])
  .filter((r) => (r.soort === 'account' || r.soort === 'leeg') && /^rec[A-Za-z0-9]{14}$/.test(r.sleutel))
  .sort((a, b) => (a.naam || '').localeCompare(b.naam || '', 'nl'));

/** Groei van RF02 (lopend jaar) naar het target (volgend jaar): bedrag en procent; null zonder beide. */
export function groei(target, rf02) {
  if (target == null || rf02 == null) return null;
  const bedrag = Number(target) - Number(rf02);
  return { bedrag, pct: Number(rf02) > 0 ? Math.round((bedrag / Number(rf02)) * 100) : null };
}

/** Gefactureerd + lopend werk, en welk deel van RF02 dat is (lopend werk kan deels al gefactureerd zijn). */
export function factuurPlusLopend(gefactureerd, lopend, rf02) {
  const som = Number(gefactureerd || 0) + Number(lopend || 0);
  return { som, pct: rf02 ? Math.round((som / Number(rf02)) * 100) : null };
}

// ── retainer en proposities (1 okt 2026) ───────────────────────────────────

export const RETAINER_FILTERS = [
  { waarde: 'alle', label: 'Alle' },
  { waarde: 'retainer', label: 'Retainer' },
  { waarde: 'geen', label: 'Geen retainer' },
];

/** Regels voor het gekozen retainer-filter (de vlag komt uit de engine: handmatig of afgeleid). */
export function filterRetainer(regels, filter) {
  if (filter === 'retainer') return (regels || []).filter((r) => r.retainer);
  if (filter === 'geen') return (regels || []).filter((r) => !r.retainer);
  return regels || [];
}

/**
 * KPI-totalen voor het gekozen retainer-filter. Zonder filter het totaal uit de engine; met filter
 * opnieuw opgeteld over de gefilterde regels, met dezelfde regels als services/budget.py
 * (RF02 zonder "wees"-regels, gat alleen waar het bekend is). De splitsing/proposities blijven
 * het engine-totaal (die tonen zelf al beide kanten).
 */
export function totalenVoorFilter(data, filter) {
  const tot = data?.totaal;
  if (!tot || !filter || filter === 'alle') return tot;
  const regels = filterRetainer(data.regels, filter);
  const som = (fn) => Math.round(regels.reduce((a, r) => a + (Number(fn(r)) || 0), 0) * 100) / 100;
  const realisatie = {};
  Object.keys(tot.realisatie || {}).forEach((jaar) => {
    realisatie[jaar] = som((r) => r.realisatie?.[jaar]);
  });
  return {
    ...tot,
    realisatie,
    realisatie_vorig: som((r) => r.realisatie_vorig),
    rf02: som((r) => (r.soort === 'wees' ? 0 : r.rf02)),
    target: som((r) => r.target),
    getekend: som((r) => r.getekend),
    pijplijn_gewogen: som((r) => r.pijplijn_gewogen),
    gat: som((r) => (r.gat === null || r.gat === undefined ? 0 : r.gat)),
  };
}

/** Label van een KPI-tegel met het actieve filter erachter: "Realisatie 2025 (retainer)". */
export function filterLabel(label, filter) {
  if (filter === 'retainer') return `${label} (retainer)`;
  if (filter === 'geen') return `${label} (geen retainer)`;
  return label;
}

export const PROPOSITIE_LABEL = {
  corporate: 'Corporate', merk: 'Merk', amc: 'AMC', intern: 'Intern', overig: 'Overig', onbekend: 'Onbekend',
};

/** Mix als tekst: "Merk 70% · AMC 30%" (grootste eerst). */
export function mixTekst(mix) {
  return Object.entries(mix || {}).sort((a, b) => b[1] - a[1])
    .map(([k, p]) => `${PROPOSITIE_LABEL[k] || k} ${p}%`).join(' · ');
}

/** Wat de propositie-cel toont: handmatige keuze, anders de grootste uit de doel-mix, anders onbekend. */
export function propositieWeergave(regel) {
  if (regel?.propositie) {
    return { tekst: PROPOSITIE_LABEL[regel.propositie] || regel.propositie, bron: 'handmatig',
      titel: 'Handmatig gekozen — telt voor 100% bij deze propositie' };
  }
  const mix = regel?.propositie_mix || {};
  if (regel?.hoofdpropositie && Object.keys(mix).length) {
    const hoofd = PROPOSITIE_LABEL[regel.hoofdpropositie] || regel.hoofdpropositie;
    const meer = Object.keys(mix).length > 1;
    return { tekst: meer ? `${hoofd} ${mix[regel.hoofdpropositie]}%` : hoofd, bron: 'afgeleid',
      titel: `Afgeleid uit de opdracht-mix (laatste 24 maanden): ${mixTekst(mix)}` };
  }
  return { tekst: 'Onbekend', bron: 'onbekend', titel: 'Geen projecten in de laatste 24 maanden — kies een propositie' };
}

/** Wat de retainer-cel toont, met de bron (handmatig/afgeleid/onbekend) voor de subtiele weergave. */
export function retainerWeergave(regel) {
  const tekst = regel?.retainer ? 'Retainer' : 'Los';
  if (regel?.retainer_bron === 'handmatig') return { tekst, bron: 'handmatig', titel: 'Handmatig ingesteld' };
  if (regel?.retainer_bron === 'afgeleid') {
    const pct = regel.retainer_aandeel != null ? ` (${regel.retainer_aandeel}% doorlopend werk)` : '';
    return { tekst, bron: 'afgeleid', titel: `Afgeleid uit de opdracht-mix van de laatste 12 maanden${pct}` };
  }
  return { tekst, bron: 'onbekend', titel: 'Geen projecten in de laatste 12 maanden — telt als geen retainer' };
}

/**
 * Waaraan een nieuwe klant gekoppeld kan worden: accounts en bedrijven zonder account zonder ingevuld
 * eigen budget (`heeft_budget` uit de engine; een rij die na een undo leeg is, telt als leeg).
 */
export const koppelDoelenNieuw = (regels) => (regels || [])
  .filter((r) => ['account', 'leeg', 'zonder'].includes(r.soort) && !r.heeft_budget)
  .sort((a, b) => (a.naam || '').localeCompare(b.naam || '', 'nl'));

/** Uitleg bij een nieuwe klant met een koppeling die niet (meer) werkt. */
export function koppelingTekst(regel) {
  switch (regel?.koppeling) {
    case 'actief': return `neemt ${regel.gekoppeld_naam} over`;
    case 'eigen_budget': return `koppeling staat stil: ${regel.gekoppeld_naam} heeft eigen budget`;
    case 'dubbel': return `koppeling staat stil: ${regel.gekoppeld_naam} hoort al bij een andere nieuwe klant`;
    case 'niet_gevonden': return 'gekoppeld account of bedrijf niet meer gevonden';
    default: return '';
  }
}
